/**
 * PresentationScreen - Member's turn to present issues
 * Shows assigned issues with 2-minute timer
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useGame } from '../context/GameContext';
import { IssuesList } from '../components/IssuesList';
import { IssuesKanban } from '../components/IssuesKanban';
import { getInitials, getAvatarColor, formatTime } from '../utils';
import type { Blocker } from '../types';
import './PresentationScreen.css';

const TIMER_DURATION = 120; // 2 minutes
const WARNING_THRESHOLD = 10; // Warning at 10 seconds

export function PresentationScreen() {
  const { state, actions } = useGame();
  const { currentMember, chouquetteMode, displayMode } = state;

  const [timeRemaining, setTimeRemaining] = useState(TIMER_DURATION);
  const [isTimerRunning, setIsTimerRunning] = useState(true);
  const [showBlockerInput, setShowBlockerInput] = useState(false);
  const [blockerText, setBlockerText] = useState('');
  const [chouquetteTriggered, setChouquetteTriggered] = useState(false);

  const blockerInputRef = useRef<HTMLInputElement>(null);

  // Timer effect
  useEffect(() => {
    if (!isTimerRunning || timeRemaining <= 0) return;

    const timer = setInterval(() => {
      setTimeRemaining((prev) => {
        if (prev <= 1) {
          setIsTimerRunning(false);
          // Chouquette mode: timer expired
          if (chouquetteMode && !chouquetteTriggered) {
            handleChouquetteDebt();
          }
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [isTimerRunning, timeRemaining, chouquetteMode, chouquetteTriggered]);

  // Handle chouquette debt (local only - no Jira ticket)
  const handleChouquetteDebt = useCallback(() => {
    if (!currentMember) return;

    setChouquetteTriggered(true);

    actions.addChouquetteDebt({
      memberId: currentMember.accountId,
      memberName: currentMember.displayName,
      timestamp: new Date(),
    });
  }, [currentMember, actions]);

  // Handle done
  const handleDone = () => {
    actions.completeTurn();
  };

  // Handle blocker
  const handleAddBlocker = () => {
    setShowBlockerInput(true);
    setTimeout(() => blockerInputRef.current?.focus(), 100);
  };

  const handleSubmitBlocker = () => {
    if (blockerText.trim() && currentMember) {
      const blocker: Blocker = {
        memberId: currentMember.accountId,
        memberName: currentMember.displayName,
        description: blockerText.trim(),
        timestamp: new Date(),
      };
      actions.addBlocker(blocker);
      setBlockerText('');
      setShowBlockerInput(false);
    }
  };

  const handleCancelBlocker = () => {
    setBlockerText('');
    setShowBlockerInput(false);
  };

  // End daily and go to recap
  const handleEndDaily = () => {
    actions.setPhase('RECAP');
  };

  // Keyboard controls
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (showBlockerInput) {
        if (e.code === 'Escape') {
          handleCancelBlocker();
        } else if (e.code === 'Enter') {
          handleSubmitBlocker();
        }
        return;
      }

      // End daily with Tab
      if (e.code === 'Tab') {
        e.preventDefault();
        handleEndDaily();
        return;
      }

      if (e.code === 'Enter') {
        handleDone();
      } else if (e.code === 'KeyB') {
        handleAddBlocker();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [showBlockerInput, blockerText]);

  if (!currentMember) {
    return (
      <div className="screen presentation-screen">
        <p>No member selected</p>
      </div>
    );
  }

  const isWarning = timeRemaining <= WARNING_THRESHOLD && timeRemaining > 0;
  const isExpired = timeRemaining === 0;

  return (
    <div className="screen presentation-screen">
      {/* Timer */}
      <div className={`timer ${isWarning ? 'warning' : ''} ${isExpired ? 'expired' : ''}`}>
        <span className="timer-label">TIME</span>
        <span className="timer-value">{formatTime(timeRemaining)}</span>
      </div>

      {/* Chouquette Alert */}
      {isExpired && chouquetteMode && (
        <div className="chouquette-alert">
          CHOUQUETTE TIME!
        </div>
      )}

      {/* Member Info */}
      <div className="member-header">
        <div
          className="member-avatar"
          style={{ backgroundColor: getAvatarColor(currentMember.displayName) }}
        >
          {getInitials(currentMember.displayName)}
        </div>
        <h2 className="member-name">{currentMember.displayName}</h2>
      </div>

      {/* Issues Display */}
      <div className="issues-container">
        <h3 className="issues-title">
          ISSUES ({currentMember.issues.length})
          <span className="issues-mode-indicator">{displayMode.toUpperCase()}</span>
        </h3>
        {displayMode === 'kanban' ? (
          <IssuesKanban issues={currentMember.issues} />
        ) : (
          <IssuesList issues={currentMember.issues} />
        )}
      </div>

      {/* Blocker Input */}
      {showBlockerInput && (
        <div className="blocker-input-container">
          <input
            ref={blockerInputRef}
            type="text"
            className="blocker-input"
            placeholder="Describe the blocker..."
            value={blockerText}
            onChange={(e) => setBlockerText(e.target.value)}
          />
          <div className="blocker-buttons">
            <button className="btn-submit" onClick={handleSubmitBlocker}>
              [ENTER] Submit
            </button>
            <button className="btn-cancel" onClick={handleCancelBlocker}>
              [ESC] Cancel
            </button>
          </div>
        </div>
      )}

      {/* Actions */}
      {!showBlockerInput && (
        <div className="presentation-actions">
          <button className="action-button action-done" onClick={handleDone}>
            [ENTER] DONE
          </button>
          <button className="action-button action-blocker" onClick={handleAddBlocker}>
            [B] BLOCKER
          </button>
        </div>
      )}

      {/* Controls */}
      <div className="presentation-controls">
        <span>[ENTER] Done</span>
        <span>[B] Add Blocker</span>
        <span>[TAB] End Daily</span>
      </div>

      {/* End Daily Button */}
      <button className="end-daily-button" onClick={handleEndDaily}>
        [TAB] END DAILY
      </button>
    </div>
  );
}

export default PresentationScreen;
